import { useState, useEffect, useRef } from 'react'
import plantDataConfigApi from '@/api/config/plantDataConfigApi'
import soilDataConfigApi from '@/api/config/soilDataConfigApi'
import pipeDataConfigApi from '@/api/config/pipeDataConfigApi'
import emitterDataConfigApi from '@/api/config/emitterDataConfig'
import areaApi from '@/api/config/areaApi'
import etZoneDataConfig from '@/api/config/etZoneDataConfig'
import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { NotifyService } from '@/helpers'
import useTranslation from '@/hooks/useTranslation'
import { fetchStationDecoderPurposeIds } from '@/utils/stationDecoderPurposes'
import { orderPipesForDropdown } from './pipe-dropdown-order'

export function useStationOptions(interfaceId: string | number | null) {
  const { t } = useTranslation()
  const [areaOptionsData, setAreaOptionsData] = useState<any[]>([])
  const [subAreaOptionsData, setSubAreaOptionsData] = useState<any[]>([])
  const [plantOptionsData, setPlantOptionsData] = useState<any[]>([])
  const [soilOptionsData, setSoilOptionsData] = useState<any[]>([])
  const [emitterOptionsData, setEmitterOptionsData] = useState<any[]>([])
  const [pipeOptionsData, setPipeOptionsData] = useState<any[]>([])
  const [etZoneOptionsData, setEtZoneOptionsData] = useState<any[]>([])
  const [controllerOptionsData, setControllerOptionsData] = useState<any[]>([])

  const subAreaCacheRef = useRef<Record<number, any[]>>({})
  /**
   * Whether the lists a station payload is checked against have been read.
   *
   * Each list falls back to [] when its request fails, and an empty list
   * looks exactly like one still loading. The save treats an empty list as
   * unknown and keeps the row's ids; this says when writing may start at all
   * (SCRUM-1032 review). `controllersLoaded` is apart because without the
   * controllers a create has no decoder id or twiId to send.
   */
  const [optionsSettled, setOptionsSettled] = useState(false)
  const [controllersLoaded, setControllersLoaded] = useState(false)

  useEffect(() => {
    if (!interfaceId) return
    let cancelled = false
    setOptionsSettled(false)
    setControllersLoaded(false)
    let anyFailed = false
    const failed = () => {
      anyFailed = true
      return []
    }

    const loadOptions = async () => {
      try {
        const areasData = await areaApi
          .getAreaParent(Number(interfaceId))
          .then((res) => res.data)
          .catch(failed)
        setAreaOptionsData(areasData)

        const subAreasData = await areaApi
          .getAll({ interfaceId: Number(interfaceId), PageSize: 0 })
          .then((res) => {
            // The list is flat: sub-areas are their own rows linked to a parent
            // by parentId (some responses also nest a `subAreas` array). Group
            // children under each parent's name so the Sub-Area dropdown can
            // offer the right options for the row's Area.
            const flat: any[] = res.data.data || []
            const byId = new Map(flat.map((a: any) => [String(a.id), a]))
            const childrenByParent = new Map<string, any[]>()

            const add = (parentName: string, sub: any) => {
              const key = String(parentName ?? '').trim()
              if (!key) return
              const list = childrenByParent.get(key) || []
              if (!list.some((s) => s.id === sub.id)) {
                list.push({ name: sub.name, id: sub.id })
              }
              childrenByParent.set(key, list)
            }

            flat.forEach((area: any) => {
              const pid = area.parentId
              if (pid != null && String(pid) !== '0' && String(pid) !== '') {
                const parent = byId.get(String(pid))
                if (parent) add(parent.name, area)
              }
              if (area.subAreas?.length) {
                area.subAreas.forEach((s: any) => add(area.name, s))
              }
            })

            return Array.from(childrenByParent.entries()).map(
              ([areaName, subAreas]) => ({ areaName, subAreas }),
            )
          })
          .catch(failed)
        setSubAreaOptionsData(subAreasData)

        const plantsData = await plantDataConfigApi
          .getAll({
            interfaceId: Number(interfaceId),
            PageSize: 0,
          })
          .then((res) => res.data.data)
          .catch(failed)
        setPlantOptionsData(plantsData)

        const soilsData = await soilDataConfigApi
          .getAll({
            interfaceId: Number(interfaceId),
            PageSize: 0,
          })
          .then((res) => res.data.data)
          .catch(failed)
        setSoilOptionsData(soilsData)

        const emittersData = await emitterDataConfigApi
          .getAll({
            interfaceId: Number(interfaceId),
            PageSize: 0,
          })
          .then((res) => res.data.data)
          .catch(failed)
        setEmitterOptionsData(emittersData)

        const pipesData = await pipeDataConfigApi
          .getPipeDropDown(Number(interfaceId))
          .then((res) => res.data)
          .catch(failed)
        // In hydraulic-tree order, each option carrying its depth for the
        // indent (QC round 2, item 5).
        setPipeOptionsData(orderPipesForDropdown(pipesData))

        const etZonesData = await etZoneDataConfig
          .getAll(String(interfaceId), { PageSize: 0 })
          .then((res) => res.data.data)
          .catch(failed)
        setEtZoneOptionsData(etZonesData)

        let controllersOk = true
        const controllersData = await controllerDataConfigApi
          .getAll({
            PageIndex: 1,
            PageSize: 0,
            Purpose: await fetchStationDecoderPurposeIds(Number(interfaceId)),
            interfaceId: Number(interfaceId),
          })
          .then((res) => res.data.data ?? [])
          .catch(() => {
            controllersOk = false
            return failed()
          })
        if (cancelled) return
        setControllerOptionsData(controllersData)
        setControllersLoaded(controllersOk)
        // A list that failed used to pass in silence, as an empty one.
        if (anyFailed) NotifyService.error(t('stationsToastLoadOptionsFailed'))
      } catch (error) {
        if (cancelled) return
        NotifyService.error(t('stationsToastLoadOptionsFailed'))
      } finally {
        if (!cancelled) setOptionsSettled(true)
      }
    }

    loadOptions()
    return () => {
      cancelled = true
    }
  }, [interfaceId, t])

  return {
    areaOptionsData,
    subAreaOptionsData,
    setSubAreaOptionsData,
    plantOptionsData,
    soilOptionsData,
    emitterOptionsData,
    pipeOptionsData,
    etZoneOptionsData,
    controllerOptionsData,
    subAreaCacheRef,
    optionsSettled,
    controllersLoaded,
  }
}
